// Ultimate Card Game (UCG) - Deterministic RNG
//
// Simulation-critical randomness (card draws, target selection, spawn
// offsets) must produce identical results on both peers in an online match,
// since only player commands travel over the network - each client
// simulates the outcome locally. mulberry32 is used because it's pure
// 32-bit integer arithmetic (Math.imul), which ECMAScript specifies
// exactly, so it can't drift between browser engines the way transcendental
// functions (sin/cos/pow) can.
//
// Purely cosmetic randomness (particle drift, wobble, dust color) keeps
// using Math.random() - it never affects match state, so it doesn't need
// to match between peers.

export class SeededRNG {
  constructor(seed) {
    this.state = seed >>> 0;
  }

  // Re-seeds this exact instance in place. Used when an online match
  // starts: Battlefield/CardSystem already hold a reference to this same
  // RNG object (passed once at Game construction), so reseeding here
  // propagates to both of them without needing to hand out a new object
  // and rewire every holder of the old one.
  reseed(seed) {
    this.state = seed >>> 0;
  }

  next() {
    this.state = (this.state + 0x6D2B79F5) | 0;
    let t = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  range(min, max) {
    return min + this.next() * (max - min);
  }

  int(minInclusive, maxExclusive) {
    return Math.floor(this.range(minInclusive, maxExclusive));
  }

  pick(array) {
    return array[this.int(0, array.length)];
  }

  chance(p) {
    return this.next() < p;
  }
}

// Same interface, backed by Math.random(). Used wherever no seeded RNG was
// supplied, so call sites never need a null check.
export const UnseededRNG = {
  next: () => Math.random(),
  range: (min, max) => min + Math.random() * (max - min),
  int: (minInclusive, maxExclusive) => Math.floor(minInclusive + Math.random() * (maxExclusive - minInclusive)),
  pick: (array) => array[Math.floor(Math.random() * array.length)],
  chance: (p) => Math.random() < p
};

// seed >>> 0 wraps to a valid 32-bit unsigned int; Date.now() alone is
// enough entropy for single-player, where determinism doesn't matter.
export function createRNG(seed) {
  return new SeededRNG(seed !== undefined ? seed : (Date.now() ^ (Math.random() * 0xffffffff)) >>> 0);
}
